import { useEffect, useRef, useState, useCallback } from 'react';

declare const __BUILD_TIME__: string;

const STORAGE_KEY = 'hk-build-time';
const CHECK_INTERVAL = 5 * 60 * 1000; // Check every 5 minutes
const DISMISSED_KEY = 'hk-update-dismissed';

/**
 * Checks if the app has been redeployed by comparing the build timestamp
 * baked into the JS bundle against what index.html currently serves.
 *
 * On every build, Vite injects a new __BUILD_TIME__. When a new deployment
 * happens, the index.html will reference new JS chunks with a different
 * build time. We detect this by fetching index.html and checking if the
 * hash of the page has changed.
 */
export const useUpdateChecker = () => {
	const [updateAvailable, setUpdateAvailable] = useState(false);
	const intervalRef = useRef<ReturnType<typeof setInterval>>();
	const initialHash = useRef<string | null>(null);

	const checkForUpdate = useCallback(async () => {
		try {
			// Fetch the current index.html with cache-busting
			const res = await fetch(`/?_t=${Date.now()}`, {
				cache: 'no-store',
				headers: { Accept: 'text/html' }
			});

			if (!res.ok) return;

			const html = await res.text();

			// Extract the script src hashes from the HTML — these change on every build
			const scriptMatches = html.match(/src="\/assets\/[^"]+"/g);
			const currentHash = scriptMatches?.join('|') || '';

			if (!initialHash.current) {
				// First check — store the baseline
				initialHash.current = currentHash;
				return;
			}

			// If the script references changed, a new build was deployed
			if (currentHash !== initialHash.current) {
				const dismissed = sessionStorage.getItem(DISMISSED_KEY);
				if (!dismissed) {
					setUpdateAvailable(true);
				}
			}
		} catch {
			// Network error — silently ignore
		}
	}, []);

	const dismiss = useCallback(() => {
		setUpdateAvailable(false);
		sessionStorage.setItem(DISMISSED_KEY, 'true');
	}, []);

	const refresh = useCallback(() => {
		window.location.reload();
	}, []);

	useEffect(() => {
		// Store the current build time
		if (typeof __BUILD_TIME__ !== 'undefined') {
			localStorage.setItem(STORAGE_KEY, __BUILD_TIME__);
		}

		// Start checking after a delay (don't check immediately on load)
		const startTimeout = setTimeout(() => {
			checkForUpdate();
			intervalRef.current = setInterval(checkForUpdate, CHECK_INTERVAL);
		}, 30_000); // Wait 30s before first check

		// Also check when tab becomes visible again
		const handleVisibility = () => {
			if (document.visibilityState === 'visible') {
				checkForUpdate();
			}
		};
		document.addEventListener('visibilitychange', handleVisibility);

		return () => {
			clearTimeout(startTimeout);
			if (intervalRef.current) clearInterval(intervalRef.current);
			document.removeEventListener('visibilitychange', handleVisibility);
		};
	}, [checkForUpdate]);

	return { updateAvailable, dismiss, refresh };
};
